import { TZDate } from '@date-fns/tz';

import { formatEventWallClock } from './event-time';

/**
 * Shared timezone-safe date utilities for feature packages.
 *
 * THE PROBLEM:
 * JavaScript's `new Date('YYYY-MM-DD')` interprets date-only strings as UTC midnight.
 * When `toLocaleDateString()` is called, it converts to local time.
 * For users in timezones west of UTC (e.g., US Pacific UTC-8), this shows
 * the wrong day: "2026-02-20" at UTC midnight becomes "Feb 19" at 4pm local.
 *
 * THE SOLUTION:
 * These utilities parse date-only strings as LOCAL midnight, ensuring the
 * displayed date matches the database date regardless of user timezone.
 */

/**
 * Parse a date string as LOCAL time (not UTC).
 *
 * This function safely handles both:
 * - Date-only strings ('2026-02-20') - parsed as local midnight
 * - ISO datetime strings ('2026-02-20T18:00:00Z') - parsed normally
 */
export function parseLocalDate(
  dateStr: string | Date | null | undefined,
): Date | null {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;

  // ISO datetime with time component - parse normally
  if (dateStr.includes('T') || dateStr.includes(' ')) {
    return new Date(dateStr);
  }

  // Date-only string (YYYY-MM-DD) - append T00:00:00 to force local interpretation
  return new Date(`${dateStr}T00:00:00`);
}

/**
 * Today's local calendar date as a `YYYY-MM-DD` string.
 *
 * Built from local date components (NOT `toISOString()`, which is UTC and
 * would report the wrong day for users east/west of UTC near midnight).
 * The result is directly comparable against PostgreSQL `date` columns
 * (`closing_date`, `audition_dates.start_date`) because date-only strings
 * sort lexicographically the same as chronologically.
 *
 * @param now - injectable clock for tests (default: `new Date()`)
 * @returns local date as `YYYY-MM-DD`
 *
 * @example
 * localTodayISODate() // "2026-06-30"
 */
export function localTodayISODate(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a date for display with timezone safety.
 *
 * @param date - Date string or Date object
 * @param options - Intl.DateTimeFormat options
 * @param locale - BCP 47 locale identifier (default: en-US)
 * @returns Formatted date string or '-' if date is null
 */
export function formatLocalDate(
  date: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  },
  locale: string = 'en-US',
): string {
  const parsed = parseLocalDate(date);
  if (!parsed) return '-';
  return parsed.toLocaleDateString(locale, options);
}

/**
 * Format date portion of a timestamp (datetime) for display.
 * Converts the timestamp to local time and formats only the date.
 *
 * Use this when you have a full timestamp but only want to display the date.
 */
export function formatTimestampDate(
  timestamp: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  locale: string = 'en-US',
): string {
  if (!timestamp) return '-';
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return date.toLocaleDateString(locale, options);
}

/** Parse a known event wall time in its IANA timezone, never the server timezone.
 * TZDate constructors and calendar arithmetic: https://github.com/date-fns/tz
 */
export function parseEventDateTime(
  date: string,
  time: string,
  timeZone: string,
): Date {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const clock = /^(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/.exec(time);
  if (!parts || !clock) throw new Error('Invalid event date or time');
  const [, year, month, day] = parts;
  const [, hour, minute, second = '0'] = clock;
  const result = new TZDate(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
    timeZone,
  );
  if (
    !Number.isFinite(result.getTime()) ||
    result.getFullYear() !== Number(year) ||
    result.getMonth() !== Number(month) - 1 ||
    result.getDate() !== Number(day) ||
    result.getHours() !== Number(hour) ||
    result.getMinutes() !== Number(minute) ||
    result.getSeconds() !== Number(second)
  ) {
    throw new Error('Invalid or nonexistent event date or time');
  }
  return new Date(result.getTime());
}

/** Set a calendar-day schedule in the event timezone, including across DST. */
export function getZonedCalendarTime(
  date: Date | string,
  daysOffset: number,
  hour: number,
  timeZone = 'UTC',
): Date {
  const zoned =
    typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
      ? new TZDate(
          parseEventDateTime(date, '12:00', timeZone).getTime(),
          timeZone,
        )
      : new TZDate(parseEventDate(date).getTime(), timeZone);
  if (
    !Number.isFinite(zoned.getTime()) ||
    !Number.isInteger(daysOffset) ||
    !Number.isInteger(hour) ||
    hour < 0 ||
    hour > 23
  )
    throw new Error('Invalid schedule date');
  zoned.setDate(zoned.getDate() + daysOffset);
  zoned.setHours(hour, 0, 0, 0);
  return new Date(zoned.getTime());
}

/** Accept actual instants or calendar dates, never server-local timestamp strings. */
export function parseEventDate(value: Date | string): Date {
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime()))
      throw new Error('Invalid event date');
    return value;
  }
  const parts =
    /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2}))?$/.exec(
      value,
    );
  if (!parts) throw new Error('Invalid event date');
  const day = parts[1]!;
  const calendar = new Date(`${day}T12:00:00Z`);
  if (
    !Number.isFinite(calendar.getTime()) ||
    calendar.toISOString().slice(0, 10) !== day ||
    Number(parts[2] ?? 0) > 23 ||
    Number(parts[3] ?? 0) > 59 ||
    Number(parts[4] ?? 0) > 59
  )
    throw new Error('Invalid event date');
  const parsed = parts[2] === undefined ? calendar : new Date(value);
  if (!Number.isFinite(parsed.getTime())) throw new Error('Invalid event date');
  return parsed;
}

/**
 * The zone in which a stored event instant is read for display.
 *
 * `events.start_date_time` is fake-UTC until the event time-zone WIP lands:
 * the `compute_event_datetime` trigger casts `event_date + start_time` in a
 * UTC session, so the stored instant carries the LOCAL wall clock labelled as
 * UTC, whatever `events.timezone` says. Reading it in UTC therefore returns
 * the wall clock exactly as entered; reading it in the event's zone shifts it
 * by the zone offset (a 15:00 Paris show would read "17:00 CET"). Every
 * user-facing event date and time reads in this zone, with no zone label.
 * The event time-zone WIP replaces this once the stored instants are real.
 */
export const EVENT_WALL_CLOCK_TIME_ZONE = 'UTC';

/**
 * @deprecated Pass `event_date` and `start_time` to `formatEventWallClock`
 * (`@kit/shared/event-time`). Kept for one release: it reads the legacy fake-UTC
 * instant and delegates, so its output equals the wall-clock formatter's.
 * Unknown dates stay absent; date-only values never acquire a fabricated time.
 */
export function formatEventDateTime(value: Date | string | null | undefined): {
  date: string;
  time: string;
} {
  if (!value) return { date: '', time: '' };
  const dateOnly =
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const iso = parseEventDate(value).toISOString();
  const { date, time } = formatEventWallClock({
    eventDate: iso.slice(0, 10),
    startTime: dateOnly ? null : iso.slice(11, 16),
  });
  return { date, time };
}
